import React, { useEffect } from "react";
import SectionHeader from "@/components/common/SectionHeader";
import { Globe2, Anchor, Ship } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1634646809203-f3b4adff9127?w=1600&q=80";
const MAP_IMG = "https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=1600&q=80";

const REGIONS = ["Asia", "Middle East", "Africa", "Europe", "Americas", "Domestic (India)"];

export default function MarketsPage() {
  useEffect(() => { document.title = "Markets & Trade — Aaha Kanakam Traders"; }, []);
  return (
    <div data-testid="markets-page">
      <section className="relative bg-forest text-white py-24 md:py-32 overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-forest via-forest/80 to-forest/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="eyebrow text-gold mb-3">Markets & Trade</div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight max-w-3xl">Domestic and international sourcing, coordinated professionally.</h1>
          <p className="mt-5 max-w-2xl text-white/70">
            We support domestic B2B requirements and explore international sourcing and supply opportunities across suitable markets — subject to product requirements, regulations and commercial feasibility.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow="Trade Coverage" title="Regions we actively explore for sourcing and supply" description="Actual shipment history will be added as trade transactions are confirmed and completed with buyers." />
            <div className="grid grid-cols-2 gap-3">
              {REGIONS.map((r) => (
                <div key={r} data-testid={`region-${r.toLowerCase().split(" ")[0]}`} className="flex items-center gap-3 p-4 bg-white border border-borderSubtle">
                  <Globe2 size={18} className="text-gold-600" />
                  <span className="text-sm font-medium text-charcoal">{r}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7 relative">
            <div className="aspect-[4/3] overflow-hidden border border-borderSubtle bg-white">
              <img src={MAP_IMG} alt="Global trade routes" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-forest text-white p-6 max-w-xs hidden md:block">
              <div className="eyebrow text-gold mb-2">Incoterms Supported</div>
              <div className="font-serif text-2xl">FOB · CIF · CFR</div>
              <p className="text-xs text-white/70 mt-2">Configurable per buyer requirement and destination port.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-surface">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 grid md:grid-cols-3 gap-5">
          <div className="p-7 bg-white border border-borderSubtle">
            <Anchor size={22} className="text-gold-600 mb-4" />
            <div className="font-serif text-xl text-forest">Indian Loading Ports</div>
            <p className="text-sm text-slateneutral mt-2 leading-relaxed">Coordinated port selection based on commodity, region and buyer preference.</p>
          </div>
          <div className="p-7 bg-white border border-borderSubtle">
            <Ship size={22} className="text-gold-600 mb-4" />
            <div className="font-serif text-xl text-forest">Container Options</div>
            <p className="text-sm text-slateneutral mt-2 leading-relaxed">20ft and 40ft FCL loading — bag, pallet or bulk configurations.</p>
          </div>
          <div className="p-7 bg-white border border-borderSubtle">
            <Globe2 size={22} className="text-gold-600 mb-4" />
            <div className="font-serif text-xl text-forest">Regulatory Awareness</div>
            <p className="text-sm text-slateneutral mt-2 leading-relaxed">Working within Indian export regulations and destination-country requirements.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
